import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  User, Mail, Calendar, Zap, FileSpreadsheet, BadgeCheck,
  Edit3, Check, X, Loader2, BarChart3, Leaf, Clock,
} from "lucide-react";
import toast from "react-hot-toast";
import Navbar from "../components/layout/Navbar";
import { getAccountMe, updateProfile } from "../api/client";
import { Skeleton, SkeletonCard, SkeletonTextBlock } from "../components/ui/Skeleton";
import "../styles/profile.css";

// ── Score pill ────────────────────────────────────────────────────────────────
function ScorePill({ score }) {
  if (score == null) return <span className="pf-score none">—</span>;
  const cls = score >= 70 ? "high" : score >= 40 ? "mid" : "low";
  return <span className={`pf-score ${cls}`}>{score}</span>;
}

// ── Status badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  if (!status) return null;
  const cls = status === "ACTIVE" ? "green" : "red";
  return <span className={`pf-badge pf-badge-${cls}`}>{status}</span>;
}

// ── Name edit field ───────────────────────────────────────────────────────────
function NameEditor({ initial, onSave }) {
  const [editing, setEditing]   = useState(false);
  const [value, setValue]       = useState(initial || "");
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");
  const inputRef                = useRef(null);

  useEffect(() => { setValue(initial || ""); }, [initial]);

  function startEdit() {
    setEditing(true);
    setError("");
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  function cancel() {
    setValue(initial || "");
    setEditing(false);
    setError("");
  }

  async function save() {
    setSaving(true);
    setError("");
    try {
      await onSave(value.trim());
      setEditing(false);
      toast.success("Name updated.");
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to save";
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  function onKeyDown(e) {
    if (e.key === "Enter") save();
    if (e.key === "Escape") cancel();
  }

  if (editing) {
    return (
      <div className="pf-name-edit">
        <input
          ref={inputRef}
          className="pf-name-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          maxLength={120}
          placeholder="Your full name"
        />
        <button className="pf-name-btn pf-name-save" onClick={save} disabled={saving}>
          {saving ? <Loader2 size={14} className="pf-spin" /> : <Check size={14} />}
        </button>
        <button className="pf-name-btn pf-name-cancel" onClick={cancel} disabled={saving}>
          <X size={14} />
        </button>
        {error && <p className="pf-name-error">{error}</p>}
      </div>
    );
  }

  return (
    <div className="pf-name-display">
      <span className="pf-name-value">{value || <em className="pf-name-placeholder">Not set</em>}</span>
      <button className="pf-name-edit-btn" onClick={startEdit} title="Edit name">
        <Edit3 size={13} />
      </button>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ProfilePage() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  async function load() {
    setLoading(true);
    setError("");
    try {
      const res = await getAccountMe();
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load profile");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleSaveName(name) {
    await updateProfile({ full_name: name });
    setData((prev) => ({
      ...prev,
      profile: { ...prev.profile, full_name: name || null },
    }));
  }

  const profile      = data?.profile;
  const subscription = data?.subscription;
  const stats        = data?.stats;
  const runs         = data?.recent_runs || [];

  function fmtDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "numeric", month: "short", year: "numeric",
    });
  }

  function fmtDateTime(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-IN", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  }

  return (
    <div className="pf-page">
      <Navbar />
      <main className="pf-main">
        <div className="pf-container">

          {/* Header */}
          <header className="pf-header">
            <div>
              <span className="pf-eyebrow">My Account</span>
              <h1 className="pf-title">Profile &amp; Settings</h1>
              <p className="pf-sub">Manage your identity, plan, and screening history.</p>
            </div>
          </header>

          {loading && (
            <div className="pf-skeleton-grid">
              <div className="pf-skeleton-left">
                {[180, 140, 140].map((h, i) => (
                  <SkeletonCard key={i} style={{ minHeight: h }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
                      <Skeleton width={40} height={40} radius={10} />
                      <div style={{ flex: 1 }}>
                        <Skeleton width="60%" height={14} radius={4} />
                        <Skeleton width="40%" height={11} radius={4} style={{ marginTop: "0.35rem" }} />
                      </div>
                    </div>
                    <SkeletonTextBlock lines={i === 0 ? 3 : i === 1 ? 2 : 4} />
                  </SkeletonCard>
                ))}
              </div>
              <div className="pf-skeleton-right">
                <SkeletonCard style={{ minHeight: 420 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
                    <Skeleton width={40} height={40} radius={10} />
                    <div style={{ flex: 1 }}>
                      <Skeleton width="50%" height={14} radius={4} />
                      <Skeleton width="35%" height={11} radius={4} style={{ marginTop: "0.35rem" }} />
                    </div>
                  </div>
                  <SkeletonTextBlock lines={6} />
                </SkeletonCard>
              </div>
            </div>
          )}

          {error && !loading && (
            <div className="pf-error">
              <X size={16} /> {error}
            </div>
          )}

          {!loading && data && (
            <>
              {/* ── Two-column layout ── */}
              <div className="pf-grid">

                {/* Left col */}
                <div className="pf-left">

                  {/* Identity card */}
                  <section className="pf-card">
                    <div className="pf-card-header">
                      <div className="pf-card-icon" style={{ background: "#dcfce7" }}>
                        <User size={18} color="#1a6b3c" />
                      </div>
                      <div>
                        <h2 className="pf-card-title">Identity</h2>
                        <p className="pf-card-sub">Your account details</p>
                      </div>
                    </div>

                    <div className="pf-fields">
                      <div className="pf-field">
                        <div className="pf-field-icon"><Mail size={14} /></div>
                        <div className="pf-field-body">
                          <span className="pf-field-label">Email</span>
                          <span className="pf-field-value pf-mono">{profile?.email || "—"}</span>
                          <span className="pf-field-hint">Sign-in address · cannot be changed</span>
                        </div>
                      </div>

                      <div className="pf-field">
                        <div className="pf-field-icon"><User size={14} /></div>
                        <div className="pf-field-body">
                          <span className="pf-field-label">Display name</span>
                          <NameEditor
                            initial={profile?.full_name}
                            onSave={handleSaveName}
                          />
                        </div>
                      </div>

                      <div className="pf-field">
                        <div className="pf-field-icon"><Calendar size={14} /></div>
                        <div className="pf-field-body">
                          <span className="pf-field-label">Member since</span>
                          <span className="pf-field-value">{fmtDate(profile?.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Plan card */}
                  <section className="pf-card">
                    <div className="pf-card-header">
                      <div className="pf-card-icon" style={{ background: "#ede9fe" }}>
                        <Zap size={18} color="#6d28d9" />
                      </div>
                      <div>
                        <h2 className="pf-card-title">Active Plan</h2>
                        <p className="pf-card-sub">Subscription &amp; quota</p>
                      </div>
                    </div>

                    {subscription ? (
                      <div className="pf-plan-body">
                        <div className="pf-plan-meta">
                          <span className="pf-plan-type">{subscription.plan_type}</span>
                          {subscription.code && (
                            <code className="pf-plan-code">{subscription.code}</code>
                          )}
                        </div>

                        <div className="pf-quota">
                          <div className="pf-quota-bar-wrap">
                            <div
                              className="pf-quota-bar"
                              style={{
                                width: subscription.max_analyses >= 9999
                                  ? "0%"
                                  : `${Math.min(100, Math.round((subscription.used_count / subscription.max_analyses) * 100))}%`,
                                background: subscription.max_analyses >= 9999 ? "#22c55e" : "#1a6b3c",
                              }}
                            />
                          </div>
                          <div className="pf-quota-labels">
                            <span>
                              {subscription.max_analyses >= 9999
                                ? "Unlimited runs"
                                : `${subscription.remaining} of ${subscription.max_analyses} runs remaining`}
                            </span>
                            <span className="pf-quota-expiry">
                              Expires {fmtDate(subscription.expiry_date)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="pf-plan-empty">
                        <p>No active plan.</p>
                        <Link to="/#access" className="pf-plan-cta">View plans →</Link>
                      </div>
                    )}
                  </section>

                  {/* Stats card */}
                  <section className="pf-card">
                    <div className="pf-card-header">
                      <div className="pf-card-icon" style={{ background: "#fef9c3" }}>
                        <BarChart3 size={18} color="#854d0e" />
                      </div>
                      <div>
                        <h2 className="pf-card-title">Usage Summary</h2>
                        <p className="pf-card-sub">All-time activity</p>
                      </div>
                    </div>

                    <div className="pf-stats-grid">
                      <div className="pf-stat">
                        <span className="pf-stat-value">{stats?.total_score_runs ?? 0}</span>
                        <span className="pf-stat-label">Total Screenings</span>
                      </div>
                      <div className="pf-stat">
                        <span className="pf-stat-value">{stats?.unique_gstins ?? 0}</span>
                        <span className="pf-stat-label">Unique GSTINs</span>
                      </div>
                      <div className="pf-stat">
                        <span className="pf-stat-value">{stats?.certs_issued ?? 0}</span>
                        <span className="pf-stat-label">Certificates</span>
                      </div>
                      <div className="pf-stat">
                        <span className="pf-stat-value">{stats?.avg_score ?? 0}</span>
                        <span className="pf-stat-label">Avg Score</span>
                      </div>
                    </div>
                  </section>
                </div>

                {/* Right col — run history */}
                <div className="pf-right">
                  <section className="pf-card pf-card-full">
                    <div className="pf-card-header">
                      <div className="pf-card-icon" style={{ background: "#e0f2fe" }}>
                        <FileSpreadsheet size={18} color="#0369a1" />
                      </div>
                      <div>
                        <h2 className="pf-card-title">Screening History</h2>
                        <p className="pf-card-sub">Your last 10 screening runs</p>
                      </div>
                    </div>

                    {runs.length === 0 ? (
                      <div className="pf-runs-empty">
                        <Leaf size={32} color="#d1fae5" />
                        <p>No screenings yet. Upload a CSV on the Screening page to get started.</p>
                        <Link to="/screening" className="pf-plan-cta">Go to Screening →</Link>
                      </div>
                    ) : (
                      <div className="pf-runs">
                        {runs.map((run) => {
                          const statusCls =
                            run.transition_status === "ELIGIBLE_FOR_CERTIFICATE" ? "eligible"
                            : run.transition_status === "TRANSITION" ? "transition"
                            : "improvement";

                          return (
                            <div key={run.id} className="pf-run">
                              <div className="pf-run-top">
                                <div className="pf-run-gstin">
                                  <span className="pf-mono pf-run-gstin-val">{run.gstin}</span>
                                  {run.transition_status && (
                                    <span className={`pf-run-status ${statusCls}`}>
                                      {run.transition_status.replace(/_/g, " ")}
                                    </span>
                                  )}
                                </div>
                                <ScorePill score={run.green_score} />
                              </div>

                              <div className="pf-run-meta">
                                {run.total_carbon > 0 && (
                                  <span><Leaf size={11} /> {run.total_carbon.toFixed(1)} kg CO₂e</span>
                                )}
                                {run.invoice_amount > 0 && (
                                  <span><FileSpreadsheet size={11} /> ₹{(run.invoice_amount / 1e5).toFixed(1)}L</span>
                                )}
                                {run.cert_id && (
                                  <span className="pf-run-cert">
                                    <BadgeCheck size={11} />
                                    <StatusBadge status={run.cert_status} />
                                  </span>
                                )}
                              </div>

                              <div className="pf-run-date">
                                <Clock size={11} /> {fmtDateTime(run.created_at)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </section>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
